import React, { useState } from "react";
import { useStoreState } from "ezStore";
import moment from "moment";
import { useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Card, Button, FormGroup } from "@cofano/cck";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ShuffleIcon from "@mui/icons-material/Shuffle";

import LastShippedProducts from "components/products/LastShippedProducts";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import UpdateCharterShipDialog from "view/charters/components/dialog/UpdateCharterShipDialog";
import VoyageRescheduleCallsDialog from "view/voyages/components/dialogs/VoyageRescheduleCallsDialog";
import { CallFromCallSideviewer } from "view/calls/components/sideviewer/useCallSideviewer";

import { usePermission, hasPluginPermission } from "imports/Utils";

import { RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { DateUtils } from "utils/DateUtils";

import { StatusType } from "__generated__/enums";
import { GetSailingReportByVoyageDocument } from "__generated__/view/sailingreports/graphql/sailingReport.queries.generated";

const CallSideviewerVoyageBlock = ({
	call,
	isShipEditable,
	refetch,
	forceReadOnly,
}: {
	call: CallFromCallSideviewer;
	isShipEditable: boolean;
	refetch: (call: CallFromCallSideviewer, isRescheduled: boolean) => void;
	forceReadOnly?: boolean;
}) => {

	const session = useStoreState(store => store.session);

	const {
		isCustomer,
		isCofanoAdmin,
		isOperator,
		isSkipperOrShipManager,
		isTerminal,
		isReadOnly,
		isMainUserLimited,
		isFinance,
		isCompanyUser,
		isAdmin,
		isRTR,
	} = usePermission();

	const isSailingReportAccessAllowed = isMainUserLimited || isOperator || isSkipperOrShipManager || isFinance || isAdmin || isCompanyUser;

	const [ rescheduleVoyageDialogOpen, openRescheduleVoyageDialog ] = useState(false);
	const [ openEditShipDialog, setOpenEditShipDialog ] = useState(false);

	const getEarliestCargoTimestamp = () => {
		const cargos = call?.cargos ?? [];

		if (cargos.length > 0) {
			return DateUtils.getTimestamp(cargos[0]?.start ?? moment())!;
		} else {
			return call.pta ?? DateUtils.getTimestamp(moment())!;
		}
	};

	const { data: sailingReport, loading } = useQuery(GetSailingReportByVoyageDocument, {
		variables: {
			voyageId: call?.voyage?.id,
		},
		skip: !isSailingReportAccessAllowed,
	});

	const hasEditShipPermission = !forceReadOnly && isShipEditable && (!isReadOnly && (isAdmin || isCofanoAdmin || isCompanyUser || isMainUserLimited));

	const purchaseCharters = call.voyage?.purchaseCharters ?? [];

	return (
		<React.Fragment>
			<Card
				title={<VoyageLink voyage={call?.voyage} />}
				subtitle={<ShipLink ship={call?.voyage.ship} />}
				icon={<DirectionsBoatIcon fontSize="small" color="primary" />}
				collapsable
				actions={[ {
					icon: <Tooltip title={intl.formatMessage({ id: "RESCHEDULE_VOYAGE_CALLS" })}>
						<ShuffleIcon fontSize="small" />
					</Tooltip>,
					onClick: () => openRescheduleVoyageDialog(true),
					hidden: forceReadOnly || hasPluginPermission(session, "useContainers") || isReadOnly || !isRTR,
				} ]}>
				<div>
					<FormGroup label={intl.formatMessage({ id: "STATUS" })} labelPlacement="left-center">
						{loading ? <CircularProgress size={16} /> : (
							<StatusChanger
								entity={{ ...call.voyage, sailingReport: sailingReport?.sailingReportByVoyage ?? undefined }}
								statusType={StatusType.Voyage}
								refetch={() => refetch(call, false)}
								showHistory />
						)}
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "SHIP" })} labelPlacement="left-center">
						<Box sx={{ display: "flex", gap: 0.5, alignItems: "center", minWidth: 0, maxWidth: "100%" }}>
							<Box sx={{  minWidth: 0,  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
								<ShipLink ship={call.voyage.ship} />
							</Box>

							{hasEditShipPermission && (
								<Tooltip title={purchaseCharters.length > 0 ? intl.formatMessage({ id: "VOYAGE_HAS_MULTIPLE_PURCHASE_CHARTERS" }) : undefined}>
									<span>
										<Button
											disabled={purchaseCharters.length > 1}
											onClick={() => setOpenEditShipDialog(true)}
											icon={<EditIcon fontSize="small" />}
											variant="text"
											size="small"
										/>
									</span>
								</Tooltip>
							)}
						</Box>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CREDITOR" })} labelPlacement="left-center">
						<RelationLink relation={call.voyage.creditor} />
					</FormGroup>

					{hasPluginPermission(session, "showLastShippedProducts") && (!isCustomer || !isTerminal) && (
						<LastShippedProducts
							voyage={call.voyage}
							ship={call.voyage.ship}
							forceReadOnly={forceReadOnly}
							refetch={() => refetch(call, false)}
						/>
					)}
				</div>
			</Card>

			<VoyageRescheduleCallsDialog
				open={rescheduleVoyageDialogOpen}
				voyage={call.voyage}
				onClose={() => openRescheduleVoyageDialog(false)}
				refetch={() => refetch(call, true)} />

			{openEditShipDialog && (
				<UpdateCharterShipDialog
					open={openEditShipDialog}
					onClose={() => setOpenEditShipDialog(false)}
					voyage={call.voyage}
					refetch={() => refetch(call, false)}
				/>
			)}
		</React.Fragment>
	);
};

export default CallSideviewerVoyageBlock;
